import { Component, Inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { SharedModule } from '../../../../shared/shared.module';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ImageService } from '../../../../../common/services/image/image.service';
import { ButtonTriggerEvent } from '../../../../../common/components/button/button.component';
import { NgIf } from '@angular/common';
import { ConfigService } from '../../../../../common/services/config/config.service';
import { Itinerary } from '../../../models/itinerary';
import { TripPlannerService } from '../../../services/trip.planner.service';
import { catchError, delay, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ColorPickerComponent } from '../../../../../common/components/color.picker/color.picker.component';
import { VisitorService } from '../../../../shared/services/visitor.service';
import { UserTrip } from '../../../../shared/models/vititor.trip.storage.command';
import { ExportPDFService } from 'src/app/modules/shared/services/export.pdf.service';
import { PendoService } from 'src/app/modules/shared/services/pendo.service';

@Component({
  selector: 'ig-share-itinerary-modal',
  standalone: true,
  imports: [MatIcon, SharedModule, NgIf],
  templateUrl: './share.itinerary.modal.component.html',
  styleUrl: './share.itinerary.modal.component.scss',
})
export class ShareItineraryModalComponent {
  form: FormGroup;
  contactForm: FormGroup;
  emailErrorMsg = ''; //'Please enter a correct email.';
  inquiryErrorMsg = '';
  shareData: ShareItineraryModalData;
  linkCopied = false;

  defaultImg = `https://iti-images.s3.amazonaws.com/events/26727d50-f9b0-5318-c4a2-4057bfd4bdef.webp`;
  defaultAvatar = `https://iti-images.s3.amazonaws.com/events/46a894bb-0ef0-270c-7118-e3bacdad1c9a.webp`;

  constructor(
    public imgService: ImageService,
    public tripService: TripPlannerService,
    public visitorService: VisitorService,
    public pdfService: ExportPDFService,
    public pendoService: PendoService,
    private _fb: FormBuilder,
    private dialogRef: MatDialogRef<ShareItineraryModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: ShareItineraryModalData,
  ) {
    this.shareData = Object.assign({}, data);

    this.form = this._fb.group({
      emails: [null, [Validators.required]],
    });

    this.contactForm = this._fb.group({
      name: [null, [Validators.required]],
      number: [null, []],
      email: new FormControl(
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(ConfigService.config.emailPattern),
        ]),
      ),
      comment: [null, [Validators.required]],
    });
  }

  sendEmail(event: ButtonTriggerEvent) {
    if (this.shareData.isEditMode) {
      alert(`You cannot share the itinerary in the edit mode.`);
      event.immediateComplete();
      return;
    }

    // const base64Imgs = await this.pdfService.cropImages(command, totalItemsCount);
    // const dynamicHTML = this.createBannerDiv(base64Imgs);

    let imgUrl = this.defaultImg;
    if (this.shareData.type.includes('trip')) {
      imgUrl = (this.shareData.itinerary as UserTrip)?.days[0]?.attractions[0]?.item?.cover?.source;
    } else {
      imgUrl = this.shareData.itinerary['image'];
    }
    imgUrl = this.imgService.imageSourceUrl(imgUrl);

    let description = '';
    if (this.shareData.type === 'trip') {
      const trip = this.shareData.itinerary as UserTrip;
      description =
        trip?.days.length > 1
          ? `${trip?.days[0]?.dateText} - ${trip?.days.last()?.dateText}`
          : `${trip?.days[0]?.dateText}`;
    } else if (this.shareData.type === 'itinerary') {
      description = this.shareData.itinerary.description;
    }

    let profilePicture = '';
    if (this.shareData.user) {
      profilePicture = this.shareData.user.profilePicture || this.defaultAvatar;
    }

    const emails = this.form.get('emails').value;
    this.emailErrorMsg = '';
    this.tripService
      .sendEmails(emails, window.location.href, undefined, this.shareData.hubName, {
        type: this.processTypeText(),
        title: this.shareData.itinerary['title'],
        userName: this.shareData.user?.userName,
        profilePicture,
        cityName: this.shareData.cityName,
      })
      .pipe(
        catchError((error) => {
          this.emailErrorMsg =
            error?.error?.message || 'Unable to send the email. Please try again later.';
          event.immediateComplete();
          return of(null);
        }),
      )
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this.pendoService.track('Send - Share Itinerary - ' + emails);
        event.success();
      });
  }

  processTypeText() {
    if (this.shareData.type === 'ai-trip') {
      return 'AI trip';
    }
    return this.shareData.type;
  }

  shareByEmailApp() {
    if (this.shareData.isEditMode) {
      alert(`You cannot share the itinerary in the edit mode.`);
      return;
    }

    window.location.href =
      this.shareData.type === 'itinerary'
        ? ConfigService.config.tripPlanner.itineraryMailTo.replace('${url}', window.location.href)
        : ConfigService.config.tripPlanner.userTripMailTo.replace('${url}', window.location.href);
  }

  sendInquiry(event: ButtonTriggerEvent) {
    if (this.shareData.isEditMode) {
      alert(`You cannot send inquiry in the edit mode.`);
      event.immediateComplete();
      return;
    }

    this.inquiryErrorMsg = '';
    this.visitorService
      .sendHelp({
        hubName: this.shareData.hubName,
        name: this.contactForm.get('name').value,
        email: this.contactForm.get('email').value,
        comment: this.contactForm.get('comment').value,
        number: this.contactForm.get('number').value,
        link: window.location.href,
      })
      .pipe(
        catchError((err) => {
          this.inquiryErrorMsg =
            err?.error?.message || 'Unable to send your inquiry. Please try again later.';
          event.immediateComplete();
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }
        event.success();
        this.pendoService.track('Send - Share Itinerary - Contact US');
      });
  }

  shareToFacebook() {
    if (this.shareData.isEditMode) {
      alert(`You cannot share the itinerary in the edit mode.`);
      return;
    }

    let link = '',
      baseURL = window.location.href.includes('localhost')
        ? 'http://localhost:3000/api/common/html'
        : 'https://api.imgoingcalendar.com/api/common/html';
    if (this.shareData.type === 'itinerary') {
      link = `${baseURL}%3Ftype=itinerary%26id=${
        this.shareData.itinerary['_id']
      }%26url=${encodeURIComponent(window.location.href)}`;
    } else {
      link = `${baseURL}%3Ftype=trip%26id=${
        this.shareData.itinerary['_id']
      }%26name=${this.shareData.itinerary['title']}%26url=${encodeURIComponent(
        window.location.href,
      )}`;
    }

    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + link,
      'facebook-share-dialog',
      'width=800,height=600',
    );
  }

  tweetEvent() {
    if (this.shareData.isEditMode) {
      alert(`You cannot share the itinerary in the edit mode.`);
      return;
    }

    const shareLink = ConfigService.config.eventShare.tweet
      .replace('${text}', this.shareData.itinerary.title)
      .replace('${url}', encodeURIComponent(window.location.href));
    window.open(shareLink, '', 'width=800,height=600');
  }

  get link() {
    if (this.shareData.type === 'itinerary' && !this.shareData.itinerary['isActive']) {
      return `Unable to generate a link for an unpublished itinerary!`;
    }
    return window.location.href;
  }

  sendRequest(event: ButtonTriggerEvent) {
    if (this.shareData.isEditMode) {
      alert(`You cannot share the itinerary in the edit mode.`);
      event.immediateComplete();
      return;
    }
    this.tripService
      .sendEmails(
        this.form.get('emails').value,
        window.location.href,
        undefined,
        this.shareData.hubName,
      )
      .subscribe((data) => {
        event.success();
      });
  }

  copy() {
    this.visitorService.copyToClip(this.link);
    this.linkCopied = true;
    setTimeout(() => {
      this.linkCopied = false;
    }, 4000);
  }

  public close(): void {
    this.dialogRef.close();
  }
}

export interface ShareItineraryModalData {
  itinerary: Itinerary | UserTrip;
  hubName: string;
  cityName: string;
  isEditMode: boolean;
  type: 'trip' | 'itinerary' | 'ai-trip';
  user: {
    profilePicture: string;
    userName: string;
  };
}
